import React from 'react';
import { Link } from 'react-router-dom';
import '../projects.css'; // Reuse the projects page styles
import '../myapps.css'; // Import the specific styles for MyApps page

const MyApps = () => {
  // App data based on the installers available
  const mobileApps = [
    {
      id: 'dockeep',
      title: 'DocKeep',
      description: 'Android application built with Kotlin for storing and organizing personal documents locally. Features Material Design and dark/light themes.',
      version: '1.0.0',
      size: '9.4 MB',
      type: 'APK',
      downloadLink: 'https://github.com/Deveshsamant/Devesh/releases/download/installers-v1/DocKeep.apk',
      image: '/images/DocKeep.png',
      features: ['Document Storage', 'Material Design', 'Dark/Light Themes', 'Local Storage'],
      stats: [
        { icon: 'fas fa-mobile-alt', text: 'Android App' },
        { icon: 'fas fa-weight-hanging', text: '9.4 MB' }
      ]
    }
  ];

  const windowsApps = [
    {
      id: 'framex',
      title: 'FrameX Media Player',
      description: 'Portable build of the Tauri media player — no installer needed, just unzip and run tauri-appframe-x.exe. Bundles libmpv for playback. AI subtitle generation additionally needs Python, FFmpeg and openai-whisper on your PATH.',
      version: '1.0.0',
      size: '95.5 MB',
      type: 'ZIP',
      downloadLink: 'https://github.com/Deveshsamant/Devesh/releases/download/installers-v1/FrameX.zip',
      image: '/images/framex.webp',
      features: ['Portable', 'libmpv Playback', 'Local AI Subtitles', 'TMDB Posters'],
      stats: [
        { icon: 'fas fa-desktop', text: 'Windows App' },
        { icon: 'fas fa-weight-hanging', text: '95.5 MB' }
      ]
    },
    {
      id: 'fileorganizer',
      title: 'FileOrganizer',
      description: 'Desktop application built with Python for organizing and managing files on Windows systems. Features file browsing and advanced sorting.',
      version: '1.0.0',
      size: '34.6 MB',
      type: 'EXE',
      downloadLink: 'https://github.com/Deveshsamant/Devesh/releases/download/installers-v1/FileOrganizer.exe',
      image: '/images/FileOrganizer.png',
      features: ['File Management', 'Advanced Sorting', 'File Browsing', 'Windows Integration'],
      stats: [
        { icon: 'fas fa-desktop', text: 'Windows App' },
        { icon: 'fas fa-weight-hanging', text: '34.6 MB' }
      ]
    },
    {
      id: 'projecttodo',
      title: 'Project To-Do Manager',
      description: 'Desktop application built with Electron.js for organizing tasks within different projects. Features drag-and-drop functionality and dark/light themes.',
      version: '1.0.0',
      size: '72.5 MB',
      type: 'EXE',
      downloadLink: 'https://github.com/Deveshsamant/Devesh/releases/download/installers-v1/Project.To-Do.Manager.Setup.1.0.0.exe',
      image: '/images/Project-To-Do-Manager.png',
      features: ['Task Management', 'Drag & Drop', 'Project Organization', 'Dark/Light Themes'],
      stats: [
        { icon: 'fas fa-tasks', text: 'Task Management' },
        { icon: 'fas fa-weight-hanging', text: '72.5 MB' }
      ]
    }
  ];

  // Function to render app cards
  // Chrome extensions are installed from the Web Store, not downloaded, so they
  // carry `external: true` — the renderer then emits a normal outbound link
  // instead of a `download` anchor, and uses the store wording.
  const chromeExtensions = [
    {
      id: 'leetsync',
      title: 'LeetSync',
      description: 'Watches your LeetCode submissions and automatically pushes every accepted solution to GitHub — the code, the full problem description, a per-problem README with badges, and a live progress dashboard. Zero clicks, 15+ languages, and your token never leaves Chrome sync storage.',
      version: '1.0.0',
      size: 'Web Store',
      type: 'Extension',
      external: true,
      downloadLink: 'https://chromewebstore.google.com/detail/neghhaodkpiafoalaeldhbnboncnalec?utm_source=item-share-cb',
      ctaLabel: 'Add chrome extension',
      ctaIcon: 'fab fa-chrome',
      categoryIcon: 'fab fa-chrome',
      categoryLabel: 'Chrome Extension',
      image: '/images/leetsync.webp',
      features: ['Fully Automatic', '15+ Languages', 'Live Dashboard', 'Private & Local'],
      stats: [
        { icon: 'fas fa-bolt', text: 'Zero Clicks' },
        { icon: 'fab fa-github', text: 'Auto Push' }
      ]
    }
  ];

  const renderAppCards = (apps) => (
    <div className="projects-grid app-cards-grid">
      {apps.map((app) => (
        <div key={app.id} className="project-card" data-category={app.type.toLowerCase()}>
          <div className="project-image">
            {app.image ? (
              <img src={app.image} alt={app.title} />
            ) : (
              <div className="project-placeholder">
                <i className={`fas ${app.type === 'APK' ? 'fa-mobile-alt' : 'fa-desktop'}`}></i>
              </div>
            )}
            <div className="project-overlay">
              <div className="project-actions">
                <a 
                  href={app.downloadLink} 
                  className="action-btn" 
                  data-tooltip={app.external ? `Open ${app.title} in the Chrome Web Store` : `Download ${app.title}`}
                  target={app.external ? '_blank' : undefined}
                  rel={app.external ? 'noopener noreferrer' : undefined}
                  download={app.external ? undefined : true}
                >
                  <i className={app.external ? (app.ctaIcon || 'fas fa-arrow-up-right-from-square') : 'fas fa-download'}></i>
                </a>
              </div>
            </div>
          </div>
          <div className="project-content">
            <div className="project-category">
              <i className={app.categoryIcon || `fas ${app.type === 'APK' ? 'fa-android' : 'fa-windows'}`}></i>
              {app.categoryLabel || `${app.type} Application`}
            </div>
            <h3 className="project-title">{app.title}</h3>
            <p className="project-description">{app.description}</p>
            
            {/* Adding project stats like in Projects page */}
            <div className="project-stats">
              {app.stats.map((stat, index) => (
                <div key={index} className="stat">
                  <i className={stat.icon}></i>
                  <span>{stat.text}</span>
                </div>
              ))}
            </div>
            
            <div className="project-tech">
              {app.features.map((feature, index) => (
                <span key={index} className="tech-tag">{feature}</span>
              ))}
            </div>
            
            <div className="app-download-button">
              <a 
                href={app.downloadLink} 
                className="btn-primary"
                target={app.external ? '_blank' : undefined}
                rel={app.external ? 'noopener noreferrer' : undefined}
                download={app.external ? undefined : true}
              >
                <i className={app.ctaIcon || 'fas fa-download'}></i>
                {app.ctaLabel || `Download ${app.type}`}
              </a>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      {/* Hero Section */}
      <section className="projects-hero">
        <div className="starfield">
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
          <div className="star"></div>
        </div>
        
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="title-main">My Applications</span>
              <span className="title-subtitle">Download & Install</span>
            </h1>
            <p className="hero-description">
              Explore my collection of mobile and desktop applications. All apps are free to download and use. 
              Simply click the download button to get started with any application.
            </p>
          </div>
        </div>
      </section>

      {/* Apps Sections - Two Column Layout */}
      <section className="projects-grid-section">
        <div className="container">
          <div className="apps-layout">
            {/* Left Column - Mobile Apps */}
            <div className="apps-column">
              <h2 className="category-title">
                <i className="fas fa-mobile-alt"></i>
                Mobile Applications
              </h2>
              {renderAppCards(mobileApps)}
            </div>
            
            {/* Right Column - Windows Apps */}
            <div className="apps-column">
              <h2 className="category-title">
                <i className="fas fa-desktop"></i>
                Windows Applications
              </h2>
              {renderAppCards(windowsApps)}
            </div>
          </div>

          {/* Chrome Extensions sits outside .apps-layout on purpose: that
              container draws a centred vertical divider spanning its full
              height, so a third row inside it would have the rule running
              straight through this card. */}
          <div className="apps-column apps-extensions">
            <h2 className="category-title">
              <i className="fab fa-chrome"></i>
              Chrome Extensions
            </h2>
            {renderAppCards(chromeExtensions)}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Want to See More?</h2>
            <p>Check out my other projects and see what else I've been working on!</p>
            <div className="cta-buttons">
              <Link to="/projects" className="btn-primary" data-tooltip="View all projects">
                <i className="fas fa-laptop-code"></i>
                View Projects
              </Link>
              <a href="https://github.com/Deveshsamant" target="_blank" rel="noopener noreferrer" className="btn-secondary" data-tooltip="View my code">
                <i className="fab fa-github"></i>
                View GitHub
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyApps;